import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import languageLocale from 'utils/LanguageLocale';
import {
    Flex, Text, Link, Divider, Box
} from 'components/ui';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';
import { space, colors } from 'style/config';
import { CONSTRUCTOR_PODS, GROUPING, getYmalGrouping } from 'constants/constructorConstants';
import { CTA } from 'constants/constructorConstants';
import { SIMPLIFIED_PDP_COMPARE } from 'constants/productCompare';
import skuHelpers from 'utils/skuHelpers';
import MediaUtils from 'utils/Media';
import isRmnCombinedCallFeatureEnabled from 'components/Rmn/utils';
import ChanelBottomBanner from 'components/ChanelBottomBanner';
import Swatches from 'components/ProductPage/Swatches/Swatches';
import DisplayName from 'components/ProductPage/DisplayName/DisplayName';
import LovesCount from 'components/ProductPage/LovesCount/LovesCount';
import RedesignedLoveCount from 'components/ProductPage/LovesCount/RedesignedLoveCount';
import Price from 'components/ProductPage/Price/Price';
import ProductCardCarousel from 'components/ProductPage/ProductCardCarousel/ProductCardCarousel';
import FrequentlyBoughtTogether from 'components/ProductPage/FrequentlyBoughtTogether';
import Highlights from 'components/ProductPage/Highlights/Highlights';
import Accordion from 'components/ProductPage/Accordion/Accordion';
import Info from 'components/ProductPage/Info';
import Ingredients from 'components/ProductPage/Ingredients/Ingredients';
import HowToUse from 'components/ProductPage/HowToUse/HowToUse';
import ProductDisclosures from 'components/ProductPage/ProductDisclosures/ProductDisclosures';
import ReviewsAnchor from 'components/ProductPage/ReviewsAnchor/ReviewsAnchor';
import LoveIcon from 'components/ProductPage/ProductLove/LoveIcon';
import LazyLoad from 'components/LazyLoad/LazyLoad';
import LayoutTop from 'components/ProductPage/LayoutTop/LayoutTop';
import StickyNavBar from 'components/ProductPage/StickyNavBar';
import CustomSets from 'components/ProductPage/CustomSets/CustomSets';
import ProductViewsCount from 'components/ProductPage/ProductViewsCount';
import HighlyRatedFor from 'components/ProductPage/RatingsAndReviews/HighlyRatedFor';
import RMNCarousel from 'components/Rmn/RMNCarousel';
import RecServiceCarousel from 'components/RecServiceCarousel';
import SimilarProductCarousel from 'components/SimilarProductCarousel';
import AssuranceZone from 'components/AssuranceZone';
import ProductPage from 'ai/components/EntryPoint/ProductPage';
import { isAIBeautyChatEnabledPDP } from 'ai/utils/aiBeautyChat';
import RmnAndPlaUtils from 'utils/rmnAndPla';
import useRegularProductProps from 'components/ProductPage/Type/RegularProduct/hooks/useRegularProductProps';
import FulfillmentSection from 'components/ProductPage/Type/RegularProduct/sections/FulfillmentSection';
import CommerceSection from 'components/ProductPage/Type/RegularProduct/sections/CommerceSection';
import SocialProofSection from 'components/ProductPage/Type/RegularProduct/sections/SocialProofSection';
import DiscoverySection from 'components/ProductPage/Type/RegularProduct/sections/DiscoverySection';
import BundleWidget from 'components/ProductPage/PdpBundling/BundleWidget';
import { showPdpBundlingSelector } from 'viewModel/selectors/testTarget/showPdpBundlingSelector';

const { Media } = MediaUtils;
const priceContainerStyle = { flexDirection: 'column', alignItems: 'baseline' };
const socialProofStyle = { paddingRight: '8px', fontSize: 'sm' };
const loveCountWrapperStyle = { position: 'relative' };
const loveCountStyle = { transform: 'translateX(-50%)', whiteSpace: 'nowrap' };

const getText = languageLocale.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR);

const PDP_REDESIGN_DIVIDER_COLOR = '#C4C4C4';
const pdpRedesignDividerStyle = { color: PDP_REDESIGN_DIVIDER_COLOR };
const pdpRedesignDividerStyles = {
    display: 'inline-block',
    width: 1,
    height: 10,
    marginLeft: '.5em',
    marginRight: '.5em',
    backgroundColor: PDP_REDESIGN_DIVIDER_COLOR
};
const PdpRedesignDivider = () => (
    <span
        aria-hidden='true'
        css={pdpRedesignDividerStyles}
    />
);

const renderProductLoveIcon = ({ quantity, currentProduct, currentSku, addPadding }) => (
    <LoveIcon
        quantity={quantity}
        currentProduct={currentProduct}
        sku={currentSku}
        loveSource='productPage'
        conversionType={CTA.MOVE_TO_LOVES}
        addPadding={addPadding}
    />
);
/* eslint-disable complexity */
const RegularProduct = ({ processSkuId }) => {
    const pdpProps = useRegularProductProps(processSkuId);
    const { showPDPTopOfPageRedesign, showDiscountedPriceWithCode } = pdpProps;
    const isSephoraCollection = pdpProps.displayFrequentlyBoughtTogether;
    const showSCPromoPrice = showDiscountedPriceWithCode && isSephoraCollection && !pdpProps.isGiftCard;
    const priceExtraProps = {
        showPDPTopOfPageRedesign,
        showDiscountedPriceWithCode: showSCPromoPrice
    };
    const isProductBundlingEnabled = Sephora.configurationSettings.pdpBundling?.isEnabled;
    const isPdpBundlingChallenger = useSelector(showPdpBundlingSelector);
    const formattedLoveCount = skuHelpers.formatLoveCount(pdpProps.productDetails?.lovesCount);
    const hasVisibleLoveCount = pdpProps.productDetails.lovesCount > 0 && !pdpProps.currentSku.isOutOfStock;
    const showMoreLikeThisCarousel = pdpProps.simplifiedPdpCompareExperience === SIMPLIFIED_PDP_COMPARE.CHALLENGER_ONE;
    const showSimplifiedCompareCarousel = Boolean(pdpProps.simplifiedPdpCompareExperience) && !showMoreLikeThisCarousel;

    const loveIcon = pdpProps.isGiftCard ? null : (
        <Box
            display='flex'
            alignItems='center'
        >
            {pdpProps.showSocialProofPDP?.challengerOne && hasVisibleLoveCount && (
                <Media lessThan='md'>
                    <Box
                        style={socialProofStyle}
                        paddingRight={space[1]}
                    >
                        {formattedLoveCount}
                    </Box>
                </Media>
            )}
            {showPDPTopOfPageRedesign ? (
                <Media greaterThanOrEqual='md'>
                    <Box css={loveCountWrapperStyle}>
                        {renderProductLoveIcon({
                            quantity: pdpProps.commerce.quantity,
                            currentProduct: pdpProps.currentProduct,
                            currentSku: pdpProps.currentSku,
                            addPadding: false
                        })}
                        <Box
                            position='absolute'
                            top='100%'
                            left='50%'
                            fontSize='xs'
                            lineHeight='tight'
                            css={loveCountStyle}
                        >
                            <RedesignedLoveCount
                                product={pdpProps.currentProduct}
                                skuId={pdpProps.currentSku.skuId}
                            />
                        </Box>
                    </Box>
                </Media>
            ) : (
                <Box>
                    {renderProductLoveIcon({
                        quantity: pdpProps.commerce.quantity,
                        currentProduct: pdpProps.currentProduct,
                        currentSku: pdpProps.currentSku,
                        addPadding: true
                    })}
                </Box>
            )}
            {!showPDPTopOfPageRedesign &&
                (pdpProps.showSocialProofPDP?.challengerOne || pdpProps.showSocialProofPDP?.challengerTwo) &&
                hasVisibleLoveCount && (
                <Media greaterThanOrEqual='md'>
                    <Box
                        style={socialProofStyle}
                        paddingLeft={space[1]}
                    >
                        {formattedLoveCount}
                    </Box>
                </Media>
            )}
        </Box>
    );

    return (
        <section className={pdpProps.hideProductTillSkuReady ? 'isDefault' : null}>
            {(pdpProps.stickyNavBarPdp?.challengerOne || pdpProps.stickyNavBarPdp?.challengerTwo) && (
                <StickyNavBar
                    experience={pdpProps.stickyNavBarPdp}
                    productName={pdpProps.productDetails.displayName || pdpProps.currentProduct.displayName}
                    brandName={pdpProps.productDetails.brand?.displayName || pdpProps.currentProduct.brand?.displayName}
                    focusedSku={pdpProps.focusedSku}
                />
            )}
            <LayoutTop
                regularProductHasMounted={pdpProps.regularProductHasMounted}
                sku={pdpProps.focusedSku}
                product={pdpProps.currentProduct}
                showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                showPDPVideoIcon={pdpProps.showPDPVideoIcon}
                displayedProductBanner={pdpProps.displayedProductBanner}
                displayedMiddleProductBanner={pdpProps.displayedMiddleProductBanner}
                displayedBottomProductBanner={pdpProps.displayedBottomProductBanner}
                preventPageRenderReport={pdpProps.preventPageRenderReport.current && !Sephora.Util.Perf.firstSpaLoadRender}
                isProductSampleEnabled={pdpProps.isProductSampleEnabled}
                content={{
                    top: (
                        <Fragment>
                            <DisplayName
                                productDisplayNameDataAt={Sephora.debug.dataAt('product_name')}
                                showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                                product={{
                                    brand: pdpProps.productDetails.brand || pdpProps.currentProduct?.brand,
                                    displayName: pdpProps.productDetails.displayName || pdpProps.currentProduct?.displayName
                                }}
                            />
                            {!pdpProps.isGiftCard && (
                                <Flex
                                    flexDirection='column'
                                    gap='.5em'
                                    fontSize='sm'
                                    marginBottom={[2, 4]}
                                    {...(showPDPTopOfPageRedesign
                                        ? { textAlign: 'left' }
                                        : { backgroundColor: colors.nearWhite, padding: 2, borderRadius: 2 })}
                                >
                                    <Flex
                                        alignItems='center'
                                        lineHeight='none'
                                    >
                                        {pdpProps.isRatingsEnabled && (
                                            <ReviewsAnchor
                                                rating={pdpProps.productDetails.rating}
                                                reviews={pdpProps.productDetails.reviews}
                                                reviewStatistics={pdpProps.reviewStatistics}
                                                initialTotalReviews={pdpProps.initialTotalReviews}
                                                showProsAndCons={pdpProps.showProsAndCons}
                                                currentProduct={pdpProps.currentProduct}
                                                showAddDropDownReviewSection={pdpProps.showAddDropDownReviewSection}
                                                showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                                                pdpRedesignDividerStyle={pdpRedesignDividerStyle}
                                            />
                                        )}
                                        {pdpProps.isQandAEnabled && !showPDPTopOfPageRedesign && (
                                            <Fragment>
                                                {pdpProps.isRatingsEnabled && (
                                                    <Text
                                                        marginX='.5em'
                                                        color='midGray'
                                                        children='|'
                                                    />
                                                )}
                                                {!pdpProps.showSocialProofPDP?.challengerOne && (
                                                    <>
                                                        <Media lessThan='md'>
                                                            <Link
                                                                data-at={Sephora.debug.dataAt('ask_question')}
                                                                padding='.5em'
                                                                margin='-.5em'
                                                                href='#QandA'
                                                                onClick={pdpProps.scrollToQandA}
                                                                css={{ position: 'relative', top: 1 }}
                                                                children={getText('askAQuestion')}
                                                            />
                                                            <Text
                                                                marginX='.5em'
                                                                color='midGray'
                                                                children='|'
                                                            />
                                                        </Media>
                                                        {!pdpProps.showSocialProofPDP?.challengerTwo && (
                                                            <Media greaterThanOrEqual='md'>
                                                                <Link
                                                                    data-at={Sephora.debug.dataAt('ask_question')}
                                                                    padding='.5em'
                                                                    margin='-.5em'
                                                                    href='#QandA'
                                                                    onClick={pdpProps.scrollToQandA}
                                                                    css={{ position: 'relative', top: 1 }}
                                                                    children={getText('askAQuestion')}
                                                                />
                                                                <Text
                                                                    marginX='.5em'
                                                                    color='midGray'
                                                                    children='|'
                                                                />
                                                            </Media>
                                                        )}
                                                    </>
                                                )}
                                            </Fragment>
                                        )}
                                        {!pdpProps.isGiftCard && (
                                            <Fragment>
                                                {!pdpProps.currentSku?.isOutOfStock &&
                                                (pdpProps.showSocialProofPDP?.challengerOne || pdpProps.showSocialProofPDP?.challengerTwo) ? (
                                                        <Fragment>
                                                            {showPDPTopOfPageRedesign && pdpProps.isRatingsEnabled && <PdpRedesignDivider />}
                                                            {pdpProps.showSocialProofPDP?.challengerOne && (
                                                                <Media lessThan='md'>
                                                                    <ProductViewsCount
                                                                        extraMargin
                                                                        transparentStyle
                                                                    />
                                                                </Media>
                                                            )}
                                                            <Media greaterThanOrEqual='md'>
                                                                <ProductViewsCount
                                                                    extraMargin
                                                                    unlockWidth
                                                                />
                                                            </Media>
                                                            {pdpProps.showSocialProofPDP?.challengerTwo && !showPDPTopOfPageRedesign && (
                                                                <Media lessThan='md'>
                                                                    <LovesCount
                                                                        product={{
                                                                            lovesCount: pdpProps.productDetails.lovesCount,
                                                                            skuId: pdpProps.currentSku.skuId,
                                                                            regularChildSkus: pdpProps.regularChildSkus
                                                                        }}
                                                                    />
                                                                </Media>
                                                            )}
                                                        </Fragment>
                                                    ) : (
                                                        !showPDPTopOfPageRedesign && (
                                                            <LovesCount
                                                                product={{
                                                                    lovesCount: pdpProps.productDetails.lovesCount,
                                                                    skuId: pdpProps.currentSku.skuId,
                                                                    regularChildSkus: pdpProps.regularChildSkus
                                                                }}
                                                            />
                                                        )
                                                    )}
                                            </Fragment>
                                        )}
                                    </Flex>
                                    {pdpProps.hasSentiments && (
                                        <HighlyRatedFor
                                            onSentimentApply={() => pdpProps.setIsSentimentSelectedFromHighlyRated(true)}
                                            showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                                        />
                                    )}
                                </Flex>
                            )}
                            {!pdpProps.isCanada && (
                                <Flex
                                    fontSize='sm'
                                    lineHeight='none'
                                    alignItems='center'
                                    {...priceContainerStyle}
                                >
                                    <Price
                                        {...pdpProps.priceProps}
                                        {...priceExtraProps}
                                    />
                                </Flex>
                            )}
                        </Fragment>
                    ),
                    bottom: (
                        <Fragment>
                            {pdpProps.isCanada && (
                                <Flex
                                    fontSize='sm'
                                    lineHeight='none'
                                    alignItems='center'
                                    {...priceContainerStyle}
                                >
                                    <Price
                                        {...pdpProps.priceProps}
                                        {...priceExtraProps}
                                    />
                                </Flex>
                            )}
                            <Swatches
                                isSmallView={pdpProps.isSmallView}
                                isSkuReady={pdpProps.isSkuReady}
                                isCustomSet={!!pdpProps.currentSku.configurableOptions}
                                loveIcon={loveIcon}
                                currentProduct={pdpProps.currentProduct}
                                colorIQMatch={{ skuId: pdpProps.profileColorIQ }}
                                basketType={pdpProps.basketType}
                                quantity={pdpProps.commerce.quantity}
                                storeId={pdpProps.preferredStoreInfo?.storeId}
                            />
                            {!!pdpProps.currentSku.configurableOptions && (
                                <CustomSets
                                    isSmallView={pdpProps.isSmallView}
                                    currentSku={pdpProps.currentSku}
                                    isSkuReady={pdpProps.isSkuReady}
                                    currentProduct={pdpProps.currentProduct}
                                    addToBasketDataAt='add_to_basket_btn_large_view'
                                    addToBasketDataAtSm='add_to_basket_btn_small_view'
                                    isRopis={pdpProps.fulfillment.selectedMethod === 'pickup'}
                                    loveIcon={loveIcon}
                                />
                            )}
                            {pdpProps.showAssuranceZone && (
                                <AssuranceZone
                                    marginY={space[3]}
                                    marginBottom={0}
                                    dataAt={Sephora.debug.dataAt('pdp_assurance_zone')}
                                />
                            )}
                            <FulfillmentSection
                                currentProduct={pdpProps.currentProduct}
                                fulfillment={pdpProps.fulfillment}
                                commerce={pdpProps.commerce}
                                isExternallySellable={pdpProps.isExternallySellable}
                                displayOrderCutoffCountdown={pdpProps.displayOrderCutoffCountdown}
                                showSocialProofPDP={pdpProps.showSocialProofPDP || {}}
                            />
                            <CommerceSection
                                isSkuReady={pdpProps.isSkuReady}
                                isSmallView={pdpProps.isSmallView}
                                currentProduct={pdpProps.currentProduct}
                                callToActionSku={pdpProps.callToActionSku}
                                basketType={pdpProps.basketType}
                                sameDayAvailable={pdpProps.fulfillment.sameDayAvailable}
                                loveIcon={loveIcon}
                                displayQuantityPickerInATB={pdpProps.displayQuantityPickerInATB}
                                commerce={pdpProps.commerce}
                                fulfillment={pdpProps.fulfillment}
                                increaseATBButtonOnPdpStyle={pdpProps.increaseATBButtonOnPdpStyle}
                                showSocialProofPDP={pdpProps.showSocialProofPDP || {}}
                                hasConfigurableOptions={!!pdpProps.currentSku.configurableOptions}
                            />
                        </Fragment>
                    )
                }}
            />
            {pdpProps.isSkuReady && (
                <>
                    <div id='details'>
                        {pdpProps.collapseHighlightsSectionPdp ? (
                            <Accordion
                                title={getText('highlights')}
                                id='highlights'
                                dataAt='highlights'
                            >
                                <Highlights
                                    itemId={pdpProps.productDetails.productId}
                                    showCleanModal={true}
                                    items={pdpProps.currentSku.highlights}
                                    hideTitle={true}
                                />
                            </Accordion>
                        ) : (
                            <Highlights
                                itemId={pdpProps.productDetails.productId}
                                showCleanModal={true}
                                items={pdpProps.currentSku.highlights}
                            />
                        )}
                        {pdpProps.isChallengerOneFragrance && !pdpProps.displayFrequentlyBoughtTogether && !pdpProps.isExternallySellable && (
                            <ProductCardCarousel {...pdpProps.useItWithCarouselProps} />
                        )}
                        <Info
                            title={getText('aboutTheProduct')}
                            currentSku={pdpProps.currentSku}
                            product={pdpProps.currentProduct}
                            isRegularProductSmallView={pdpProps.isSmallView}
                            description={pdpProps.productDetails.longDescription || pdpProps.currentProduct?.longDescription}
                            dataAt='about_the_product_title'
                        />
                    </div>
                    <Ingredients content={pdpProps.currentSku.ingredientDesc} />
                    <HowToUse
                        currentSku={pdpProps.currentSku}
                        content={pdpProps.productDetails.suggestedUsage}
                    />
                    {isProductBundlingEnabled && isPdpBundlingChallenger && (
                        <LazyLoad
                            component={BundleWidget}
                            currentProduct={pdpProps.currentProduct}
                        />
                    )}
                    <ProductDisclosures
                        parentCategory={pdpProps.currentProduct?.parentCategory}
                        show={pdpProps.showProductDisclosures}
                        content={pdpProps.currentSku.skuDisclosures}
                    />
                    {!pdpProps.isChallengerOneFragrance &&
                        !pdpProps.isSmallView &&
                        pdpProps.isCanada &&
                        !pdpProps.displayFrequentlyBoughtTogether &&
                        !pdpProps.isExternallySellable && <ProductCardCarousel {...pdpProps.useItWithCarouselProps} />}
                    {!pdpProps.isSmallView &&
                        pdpProps.isCanada &&
                        pdpProps.displayFrequentlyBoughtTogether &&
                        !pdpProps.isExternallySellable &&
                        !isProductBundlingEnabled && (
                        <FrequentlyBoughtTogether
                            currentSku={pdpProps.currentSku}
                            currentProduct={pdpProps.currentProduct}
                            isCarousel={true}
                        />
                    )}
                    <div id='similar'>
                        {showMoreLikeThisCarousel && (
                            <RecServiceCarousel
                                params={pdpProps.params}
                                podId={CONSTRUCTOR_PODS.SIMILAR_PRODUCTS}
                                grouping={GROUPING.SIMPLIFIED_PDP_COMPARE}
                                carouselContextId={pdpProps.productDetails.productId}
                                useSimplifiedCarouselTracking={true}
                            />
                        )}
                        {showSimplifiedCompareCarousel && (
                            <SimilarProductCarousel
                                isSmallView={pdpProps.isSmallView}
                                currentProduct={pdpProps.currentProduct}
                                simplifiedCompareVersion={pdpProps.simplifiedPdpCompareExperience}
                            />
                        )}
                    </div>
                    {(pdpProps.isSmallView || (pdpProps.isUsa && !pdpProps.isSmallView)) && !pdpProps.isExternallySellable && (
                        <pdpProps.CarouselComponent
                            params={pdpProps.params}
                            podId={CONSTRUCTOR_PODS.YMAL}
                            grouping={getYmalGrouping(pdpProps.isCarouselsATBEnabled)}
                            carouselContextId={pdpProps.productDetails.productId}
                        />
                    )}
                    {pdpProps.showRmnCarousel && (
                        <LazyLoad
                            component={RMNCarousel}
                            // eslint-disable-next-line camelcase
                            targets={{ match_products: [pdpProps.productDetails.productId] }}
                            source={RmnAndPlaUtils.RMN_SOURCES.PDP}
                            currentProductId={pdpProps.productDetails.productId}
                        />
                    )}
                    {isRmnCombinedCallFeatureEnabled() && pdpProps.showRmnBanners && pdpProps.renderRmnMidPageBanner()}
                    {!pdpProps.isChallengerOneFragrance &&
                        (pdpProps.isSmallView || (pdpProps.isUsa && !pdpProps.isSmallView)) &&
                        !pdpProps.displayFrequentlyBoughtTogether &&
                        !pdpProps.isExternallySellable && <ProductCardCarousel {...pdpProps.useItWithCarouselProps} />}
                    {(pdpProps.isSmallView || (pdpProps.isUsa && !pdpProps.isSmallView)) &&
                        pdpProps.displayFrequentlyBoughtTogether &&
                        !pdpProps.isExternallySellable &&
                        !isProductBundlingEnabled && (
                        <FrequentlyBoughtTogether
                            currentSku={pdpProps.currentSku}
                            currentProduct={pdpProps.currentProduct}
                            isCarousel={true}
                        />
                    )}
                    <SocialProofSection
                        {...pdpProps.socialProofProps}
                        isRatingsEnabled={pdpProps.isRatingsEnabled && !pdpProps.isGiftCard}
                        isQandAEnabled={pdpProps.isQandAEnabled}
                        aiBeautyChat={
                            isAIBeautyChatEnabledPDP(pdpProps.currentProduct) && (
                                <>
                                    {!pdpProps.isSmallView && <Divider marginBottom={24} />}
                                    <ProductPage />
                                </>
                            )
                        }
                    />
                    {pdpProps.isChanel && pdpProps.isSmallView && <ChanelBottomBanner marginBottom={4} />}
                    <DiscoverySection
                        currentProduct={pdpProps.currentProduct}
                        productDetails={pdpProps.productDetails}
                        currentSku={pdpProps.currentSku}
                        isSmallView={pdpProps.isSmallView}
                        isChanel={pdpProps.isChanel}
                        isGiftCard={pdpProps.isGiftCard}
                        brandData={pdpProps.brandData}
                    />
                    {!pdpProps.isSmallView && pdpProps.isCanada && !pdpProps.isExternallySellable && (
                        <pdpProps.CarouselComponent
                            params={pdpProps.params}
                            podId={CONSTRUCTOR_PODS.YMAL}
                            grouping={getYmalGrouping(pdpProps.isCarouselsATBEnabled)}
                            carouselContextId={pdpProps.productDetails.productId}
                        />
                    )}
                </>
            )}
        </section>
    );
};

export default wrapFunctionalComponent(RegularProduct, 'RegularProduct');
